import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { AppDataState, AppThunkAPIType, unknownError } from "../../app/appTypes";
import { RootState } from "../../app/store";
import { getWithAuth } from "../../http";
import { logout, selectUserCompanyId } from "../user/userSlice";

export type UserId = string;

export type Role = "admin" | "user" | "super"

export interface User {
    id: string
    userName: string
    firstname: string
    surname: string
    email: string
    avatarUrl?: string
    companyId: 0
    companyName: string
    position: string
}

export type UserWithoutLock = Omit<User, "lockedBy" | "lockReason" | "lockedAt">

export type DraftUserWithoutLock = Omit<User, "id" | "lockedBy" | "lockReason" | "lockedAt" | "expiryDate">

export interface UserEmailType {
    email: string
}

export interface UserBlockType {
    email: string,
    lockreason: string
}

export interface UserArchiveType {
    email: string,
    reason: string
    password: string
}

export const adapter = createEntityAdapter<User>({
    selectId: (user) => user.id,
    sortComparer: (a, b) => a.surname.localeCompare(b.surname),
})

export interface UsersState {
    items: EntityState<User> & { status: AppDataState };
}

const initialState: UsersState = {
    items: adapter.getInitialState({
        status: {
            type: "empty",
        }
    }),
}

export const loadUsers = createAsyncThunk<User[], void, AppThunkAPIType>("users/load", async (_, { dispatch, rejectWithValue, requestId, getState }) => {
    const { status } = getState().users.items
    const companyId = selectUserCompanyId(getState())

    switch (status.type) {
        case "loading":
            if (status.requestId === requestId) {
                const result = await dispatch(getWithAuth({ url: `api/users/company/${companyId ?? '0'}` }))
                const { payload } = result
                if (getWithAuth.fulfilled.match(result)) {
                    return payload as User[]
                } else {
                    return rejectWithValue(payload ?? { kind: 'unknown' })
                }
            } else {
                return []
            }
        default:
            return []
    }
})


export const usersSlice = createSlice({
    name: "users",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(loadUsers.pending, (state, action) => {
            const { status } = state.items
            if (status.type === "empty") {
                state.items.status = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })
        builder.addCase(loadUsers.fulfilled, (state, action) => {
            const { status } = state.items
            if (status.type === "loading" && status.requestId === action.meta.requestId) {
                adapter.setAll(state.items, action.payload)
                state.items.status = {
                    type: "loaded",
                }
            }
        })
        builder.addCase(loadUsers.rejected, (state, action) => {
            const { status } = state.items
            if (status.type === "loading" && status.requestId === action.meta.requestId) {
                state.items.status = { 
                    type: "error",
                    error: action.payload ?? unknownError()
                }
            }
        })
        builder.addCase(logout.fulfilled, (state, action) => {
            adapter.removeAll(state.items)
            state.items.status = {
                type: "empty",
            }
        })
    },
})

export const selectUsers = (state: RootState) => state.users

export const selectUserByName = (state: RootState, userName: string) => selectAllUsers(state).find(i => i.userName === userName)

export const { 
    selectAll: selectAllUsers, 
    selectById: selectUserById 
} = 
    adapter.getSelectors<RootState>((state) => state.users.items)


export default usersSlice.reducer
